// Playbook Library page — FE-044.
//
// Route: ``/dashboard/playbooks/``. Lists system + custom Playbooks
// for the broker entity, filtered by ``all | system | custom |
// archived``. The "+ New Playbook" affordance creates a fresh
// custom Playbook (POST /api/v1/playbooks/) and routes the user to
// the canonical editor at ``/dashboard/playbooks/{id}/edit``.
//
// The Apply modal can be opened from the library; final submission
// remains disabled until BE-046 ships the apply controller.

import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { PlaybookListFilters } from "./PlaybookListFilters";
import { PlaybookListTable } from "./PlaybookListTable";
import { ApplyPlaybookModal } from "./ApplyPlaybookModal";
import {
  useArchivePlaybookMutation,
  useCreatePlaybookMutation,
  useDeletePlaybookMutation,
  useDuplicatePlaybookMutation,
  usePlaybookListQuery,
  useRestorePlaybookMutation,
} from "./hooks";
import type { PlaybookListRow, PlaybookStatusFilter } from "./types";


const VALID_STATUSES: PlaybookStatusFilter[] = [
  "all", "system", "custom", "archived",
];


function parseStatus(raw: string | null): PlaybookStatusFilter {
  if (raw && (VALID_STATUSES as string[]).includes(raw)) {
    return raw as PlaybookStatusFilter;
  }
  return "all";
}


export function PlaybookListPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const status = useMemo(
    () => parseStatus(search.get("status")),
    [search],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [applyingPlaybook, setApplyingPlaybook] =
    useState<PlaybookListRow | null>(null);
  const list = usePlaybookListQuery(status);
  const createMut = useCreatePlaybookMutation();
  const dupMut = useDuplicatePlaybookMutation();
  const archiveMut = useArchivePlaybookMutation();
  const restoreMut = useRestorePlaybookMutation();
  const deleteMut = useDeletePlaybookMutation();

  const handleStatus = useCallback(
    (next: PlaybookStatusFilter) => {
      const sp = new URLSearchParams(search);
      if (next === "all") sp.delete("status");
      else sp.set("status", next);
      setSearch(sp, { replace: false });
    },
    [search, setSearch],
  );

  const handleCreate = useCallback(async () => {
    setPageError(null);
    try {
      const created = await createMut.mutateAsync({
        name: "Untitled Playbook",
        applicable_scopes: ["account"],
      });
      navigate(`/dashboard/playbooks/${created.playbook_id}/edit`);
    } catch (err: unknown) {
      setPageError(
        err instanceof Error ? err.message : "Couldn't create Playbook.",
      );
    }
  }, [createMut, navigate]);

  const handleOpenEditor = useCallback(
    (row: PlaybookListRow) => {
      navigate(`/dashboard/playbooks/${row.playbook_id}/edit`);
    },
    [navigate],
  );

  const handleApply = useCallback((row: PlaybookListRow) => {
    setApplyingPlaybook(row);
  }, []);

  const guarded = useCallback(
    async (row: PlaybookListRow, fn: () => Promise<unknown>) => {
      setBusyId(row.playbook_id);
      setPageError(null);
      try {
        await fn();
      } catch (err: unknown) {
        setPageError(
          err instanceof Error ? err.message : "Action failed.",
        );
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  const handleDuplicate = useCallback(
    (row: PlaybookListRow) =>
      guarded(row, async () => {
        const created = await dupMut.mutateAsync(row.playbook_id);
        navigate(`/dashboard/playbooks/${created.playbook_id}/edit`);
      }),
    [dupMut, guarded, navigate],
  );
  const handleArchive = useCallback(
    (row: PlaybookListRow) =>
      guarded(row, () => archiveMut.mutateAsync(row.playbook_id)),
    [archiveMut, guarded],
  );
  const handleRestore = useCallback(
    (row: PlaybookListRow) =>
      guarded(row, () => restoreMut.mutateAsync(row.playbook_id)),
    [restoreMut, guarded],
  );
  const handleDelete = useCallback(
    (row: PlaybookListRow) =>
      guarded(row, () => deleteMut.mutateAsync(row.playbook_id)),
    [deleteMut, guarded],
  );

  return (
    <div data-testid="playbook-list-page" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            Playbooks
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-secondary">
            Reusable templates that materialize into Commitments when
            applied to an Account or Plan Year.
          </p>
        </div>
        <button
          type="button"
          data-testid="playbook-list-new-button"
          onClick={handleCreate}
          disabled={createMut.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90 disabled:opacity-60"
        >
          + New Playbook
        </button>
      </header>

      <PlaybookListFilters status={status} onChange={handleStatus} />

      {pageError && (
        <div
          role="alert"
          data-testid="playbook-list-error"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
        >
          {pageError}
        </div>
      )}

      {list.isLoading ? (
        <DashboardLoadingState variant="table" rows={5} />
      ) : list.isError ? (
        <DashboardErrorState
          title="Couldn't load Playbooks"
          description="Refresh to retry, or check the network connection."
          retry={() => list.refetch()}
        />
      ) : list.data && list.data.rows.length > 0 ? (
        <PlaybookListTable
          rows={list.data.rows}
          busyId={busyId}
          onOpenEditor={handleOpenEditor}
          onApply={handleApply}
          onDuplicate={handleDuplicate}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={handleDelete}
        />
      ) : (
        <PlaybookEmptyState status={status} onCreate={handleCreate} />
      )}

      <ApplyPlaybookModal
        playbook={applyingPlaybook}
        onClose={() => setApplyingPlaybook(null)}
      />
    </div>
  );
}


function PlaybookEmptyState({
  status, onCreate,
}: { status: PlaybookStatusFilter; onCreate: () => void }) {
  if (status === "archived") {
    return (
      <DashboardEmptyState
        title="No archived Playbooks"
        description="Archive a custom Playbook to hide it from the active list."
      />
    );
  }
  if (status === "system") {
    return (
      <DashboardEmptyState
        title="No system Playbooks installed"
        description="System Playbooks are seeded with the broker entity."
      />
    );
  }
  if (status === "custom") {
    return (
      <DashboardEmptyState
        title="No custom Playbooks yet"
        description="Create your first Playbook to capture a repeatable process."
        action={
          <button
            type="button"
            data-testid="playbook-empty-new-button"
            onClick={onCreate}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            + New Playbook
          </button>
        }
      />
    );
  }
  return (
    <DashboardEmptyState
      title="No Playbooks yet"
      description="Once you add a Playbook it appears here."
      action={
        <button
          type="button"
          data-testid="playbook-empty-new-button"
          onClick={onCreate}
          className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
        >
          + New Playbook
        </button>
      }
    />
  );
}
